import React from "react";
import type { ToolCall } from "./toolCalls";
import { humanizeToolName, formatToolArgs } from "./toolCalls";
import {
  ToolCallDetails,
  ToolCallSummary,
  ToolCallBlock,
  ToolCallBlockLabel,
  ToolCallPre,
  ToolCallResponseScroll,
  ToolCallDocChunk,
  ToolCallDocTitle,
  ToolCallSpinner
} from "./toolCallStyles";

interface DocChunk {
  title: string;
  content: string;
}

const parseDocChunks = (text: string): DocChunk[] => {
  const chunks: DocChunk[] = [];
  const parts = text.split(/\n---\s+(.+?)\s+---\n/);
  for (let i = 1; i < parts.length; i += 2) {
    const title = parts[i];
    const content = (parts[i + 1] ?? "").trim();
    if (title) {
      chunks.push({ title, content });
    }
  }
  return chunks;
};

const ToolCallItem: React.FC<{ tc: ToolCall }> = ({ tc }) => {
  const isRunning = tc.status === "running";
  const docChunks = tc.result ? parseDocChunks(tc.result) : [];

  return (
    <ToolCallDetails>
      <ToolCallSummary>
        {isRunning ? <ToolCallSpinner /> : <span>&#10003;</span>}
        {humanizeToolName(tc.name)}
      </ToolCallSummary>
      <ToolCallBlock>
        <ToolCallBlockLabel>Call</ToolCallBlockLabel>
        <ToolCallPre>
          {tc.name}({formatToolArgs(tc.arguments)})
        </ToolCallPre>
      </ToolCallBlock>
      {tc.result && (
        <ToolCallBlock>
          <ToolCallBlockLabel>Response</ToolCallBlockLabel>
          {docChunks.length > 0 ? (
            <ToolCallResponseScroll>
              {docChunks.map((chunk, i) => (
                <ToolCallDocChunk key={i}>
                  <ToolCallDocTitle>{chunk.title}</ToolCallDocTitle>
                  <ToolCallPre>{chunk.content}</ToolCallPre>
                </ToolCallDocChunk>
              ))}
            </ToolCallResponseScroll>
          ) : (
            <ToolCallPre>{tc.result}</ToolCallPre>
          )}
        </ToolCallBlock>
      )}
    </ToolCallDetails>
  );
};

const ToolCallSection: React.FC<{ toolCalls: ToolCall[] }> = ({
  toolCalls
}) => {
  if (toolCalls.length === 0) {
    return null;
  }

  return (
    <div style={{ marginBottom: "0.5rem" }}>
      {toolCalls.map((tc, i) => (
        <ToolCallItem key={`${tc.name}-${i}`} tc={tc} />
      ))}
    </div>
  );
};

export default ToolCallSection;
